import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "../data/content";
import { Reveal, MaskLine, EASE } from "../lib/motion";

// The PHP handler is served from the same domain as this build, so no origin needed.
const API = "/api";

// `website` is the honeypot: hidden from users, filled in by bots.
const empty = { name: "", email: "", phone: "", company: "", service: "", message: "", website: "" };

const field =
  "w-full rounded-lg border border-white/12 bg-[#0d0d0d] px-4 py-3.5 text-sm text-white placeholder:text-neutral-500 outline-none transition-colors duration-200 focus:border-accent";

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries.php`, form);
      toast.success("Thank you! Our team will reach out to you shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden border-b border-white/10 pt-36 pb-16 md:pt-44 md:pb-20">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(37,99,235,0.18),_transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.7 }} className="mb-6 text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-accent">
            Request a Quote
          </motion.p>
          <h1 className="font-display text-5xl font-black leading-[0.92] tracking-tighter md:text-7xl lg:text-8xl">
            <MaskLine delay={0.25}>Let&apos;s talk.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7, ease: EASE }} className="mt-8 max-w-2xl text-base md:text-lg text-neutral-300 leading-relaxed">
            Tell us what you need - housekeeping, security, healthcare staffing, technical or industrial workforce - and our team will get back to you with a tailored proposal.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          {/* Contact info */}
          <Reveal className="space-y-8">
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">Reach us directly</h3>
              <div className="mt-6 space-y-6">
                <a href={`tel:${COMPANY.phone}`} data-testid="contact-phone" className="group flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/12 text-accent"><Phone size={18} /></span>
                  <span>
                    <span className="block text-xs text-neutral-500">Phone</span>
                    <span className="text-base font-semibold group-hover:text-accent transition-colors">{COMPANY.phoneDisplay}</span>
                  </span>
                </a>
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="group flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/12 text-accent"><Mail size={18} /></span>
                  <span>
                    <span className="block text-xs text-neutral-500">Email</span>
                    <span className="text-base font-semibold break-all group-hover:text-accent transition-colors">{COMPANY.email}</span>
                  </span>
                </a>
                <div className="flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/12 text-accent"><MapPin size={18} /></span>
                  <span>
                    <span className="block text-xs text-neutral-500">Head office</span>
                    <span className="text-sm leading-relaxed text-neutral-300">{COMPANY.address}</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-bold">Cities covered</p>
              <p className="font-display mt-3 text-2xl font-bold">{COMPANY.cities.join(" - ")}</p>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 md:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs text-neutral-400">Full name *</label>
                  <input data-testid="input-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className="mb-2 block text-xs text-neutral-400">Phone *</label>
                  <input data-testid="input-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="Phone number" />
                </div>
                <div>
                  <label className="mb-2 block text-xs text-neutral-400">Email *</label>
                  <input data-testid="input-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                </div>
                <div>
                  <label className="mb-2 block text-xs text-neutral-400">Company</label>
                  <input data-testid="input-company" className={field} value={form.company} onChange={set("company")} placeholder="Company name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs text-neutral-400">Service needed</label>
                  <select data-testid="input-service" className={`${field} appearance-none`} value={form.service} onChange={set("service")}>
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Multiple / Other">Multiple / Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs text-neutral-400">Message *</label>
                  <textarea data-testid="input-message" rows={5} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your requirement, site and location..." />
                </div>
              </div>

              {/* Honeypot: off-screen rather than display:none, which some bots skip. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={set("website")}
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
              />

              <button
                type="submit"
                data-testid="submit-inquiry"
                disabled={loading}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 text-sm font-bold text-black transition-transform duration-200 hover:scale-[0.99] disabled:opacity-60 sm:w-auto"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <>Send inquiry <ArrowRight size={16} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
